// src/pages/public/CaseStudies/CaseStudiesPage.jsx
import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import { fetchCaseStudies } from '../../../redux/slices/caseStudiesSlice.js'
import SEO from '../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../components/common/LazyImage.jsx'
import EmptyState from '../../../components/common/EmptyState.jsx'
import LoadMoreButton from '../../../components/common/LoadMoreButton.jsx'
import BuildSolution from '../../../components/common/Common/BuildSolution/BuildSolution.jsx'

import './CaseStudiesPage.scss'
import PageCards from '../../../components/common/PageCards.jsx'
import PageTestimonialsCarousel from '../../../components/sections/PageTestimonialsCarousel.jsx'

export default function CaseStudiesPage() {
    const dispatch = useAppDispatch()
    const { caseStudies, loading, hasMore } = useAppSelector((state) => state.caseStudies)

    const { currentPage: termsPage, loading: pageLoading } = useAppSelector((state) => state.cms)

    const [filters, setFilters] = useState({
        category: '',
        isFeatured: '',
    })

    // Client-side filtering
    const [filteredCaseStudies, setFilteredCaseStudies] = useState([])

    useEffect(() => {
        dispatch(fetchPageBySlug('case'))
    }, [dispatch])

    const getSectionData = (sectionKey) => {
        return termsPage?.sections?.find(section => section.key === sectionKey)?.data
    }

    const heroSection = getSectionData('cardsSection')

    useEffect(() => {
        dispatch(fetchCaseStudies({ page: 1, limit: 12 }))
    }, [dispatch])

    // Update filtered case studies when caseStudies or filters change
    useEffect(() => {
        if (!caseStudies || caseStudies.length === 0) {
            setFilteredCaseStudies([])
            return
        }

        let filtered = [...caseStudies]

        // Apply category filter
        if (filters.category && filters.category !== '') {
            filtered = filtered.filter(caseStudy =>
                caseStudy.category === filters.category
            )
        }

        // Apply featured filter
        if (filters.isFeatured && filters.isFeatured !== '') {
            const isFeaturedValue = filters.isFeatured === 'true'
            filtered = filtered.filter(caseStudy =>
                caseStudy.isFeatured === isFeaturedValue
            )
        }

        setFilteredCaseStudies(filtered)
    }, [caseStudies, filters])

    const loadMore = () => {
        const nextPage = Math.ceil(caseStudies.length / 12) + 1
        dispatch(fetchCaseStudies({ page: nextPage, limit: 12 }))
    }

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }))
    }

    // Extract unique categories from case studies
    const categoryOptions = useMemo(() => {
        if (!caseStudies || caseStudies.length === 0) return [{ value: '', label: 'All Categories' }]

        const categories = [...new Set(caseStudies
            .map(caseStudy => caseStudy.category)
            .filter(Boolean)
        )].sort()

        return [
            { value: '', label: 'All Categories' },
            ...categories.map(cat => ({ value: cat, label: cat }))
        ]
    }, [caseStudies])

    // Extract featured status options
    const featuredOptions = [
        { value: '', label: 'All Case Studies' },
        { value: 'true', label: 'Featured Only' },
        { value: 'false', label: 'Non-Featured Only' }
    ]

    // Reset all filters
    const resetFilters = () => {
        setFilters({
            category: '',
            isFeatured: '',
        })
    }

    return (
        <>
            <SEO
                title="Case Studies"
                description="Explore our successful projects and see how we've helped businesses achieve digital transformation and growth."
            />

            <div className="case-studies-page-root">
                {/* =========================
            HERO – v1 style
           ========================= */}
                <section className="industry-hero-section-case">
                    <div className="industry-hero-container">
                        <motion.h1
                            className="industry-heading"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            OUR EXCLUSIVE CASE STUDIES
                        </motion.h1>

                    </div>
                </section>

                {/* =========================
            GRID + FILTERS – v1 style
           ========================= */}
                <section className="case-studies-grid-section">
                    <div className="case-studies-grid-container">
                        {/* Filters */}
                        <motion.div
                            className="filters-container"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="filters-row">
                                {/* Category Filter */}
                                <select
                                    value={filters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                    className="filter-select"
                                >
                                    {categoryOptions.map((opt) => (
                                        <option key={opt.value || 'all-cat'} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>

                                {/* Featured Filter */}
                                <select
                                    value={filters.isFeatured}
                                    onChange={(e) => handleFilterChange('isFeatured', e.target.value)}
                                    className="filter-select"
                                >
                                    {featuredOptions.map((opt) => (
                                        <option key={opt.value || 'all-feat'} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>

                            </div>

                            {/* Active filters indicator */}
                            {/* {(filters.category !== '' || filters.isFeatured !== '') && (
                                <div className="active-filters">
                                    <span className="filter-label">Active Filters:</span>
                                    {filters.category !== '' && (
                                        <span className="filter-tag">
                                            Category: {categoryOptions.find(opt => opt.value === filters.category)?.label}
                                            <button
                                                onClick={() => handleFilterChange('category', '')}
                                                className="filter-tag-remove"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    )}
                                    {filters.isFeatured !== '' && (
                                        <span className="filter-tag">
                                            Status: {featuredOptions.find(opt => opt.value === filters.isFeatured)?.label}
                                            <button
                                                onClick={() => handleFilterChange('isFeatured', '')}
                                                className="filter-tag-remove"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    )}
                                </div>
                            )} */}
                        </motion.div>

                        {/* Results count */}
                        {/* {!loading && caseStudies.length > 0 && (
                            <div className="results-count">
                                Showing {filteredCaseStudies.length} of {caseStudies.length} case studies
                            </div>
                        )} */}

                        {/* Grid */}
                        {loading && caseStudies.length === 0 ? (
                            <div className="case-studies-grid">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="case-study-card skeleton">
                                        <div className="case-study-image skeleton-bg" />
                                        <div className="case-study-content">
                                            <div className="skeleton-line skeleton-line-lg" />
                                            <div className="skeleton-line" />
                                            <div className="skeleton-line short" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredCaseStudies.length > 0 ? (
                            <>
                                <div className="case-studies-grid">
                                    {filteredCaseStudies.map((caseStudy) => (
                                        <CaseStudyCard key={caseStudy._id} caseStudy={caseStudy} />
                                    ))}
                                </div>

                                {hasMore && filters.category === '' && filters.isFeatured === '' && (
                                    <LoadMoreButton
                                        hasMore={hasMore}
                                        loading={loading}
                                        onLoadMore={loadMore}
                                    />
                                )}
                            </>
                        ) : (
                            <EmptyState
                                title="No Case Studies Found"
                                message="We couldn't find any case studies matching your criteria. Try adjusting your filters."
                                action="Clear All Filters"
                                onAction={resetFilters}
                            />
                        )}
                    </div>
                </section>
            </div>
            <BuildSolution />
            <PageTestimonialsCarousel />
            <section className="services-extra-section">
                <div className="services-extra-container">
                    {heroSection?.heading && (
                        <h2 className="extra-heading">{heroSection.heading?.toUpperCase()}</h2>
                    )}
                    <PageCards />
                </div>
            </section>
        </>
    )
}

// ==========================
// Case Study Card – v1 style
// ==========================
function CaseStudyCard({ caseStudy }) {
    const capsules = []

    if (caseStudy.category) capsules.push(caseStudy.category)
    if (caseStudy.isFeatured) capsules.push("Featured")

    const imageSrc = caseStudy.featuredImage || caseStudy?.hero?.imageUrl
    const title = caseStudy.title
    const description = caseStudy.excerpt || caseStudy.shortDescription

    return (
        <Link to={`/case-studies/${caseStudy.slug}`} className="group block">
            <motion.div
                className="case-study-card"
                whileHover={{ y: -8 }}
                transition={{ duration: 0.2 }}
            >
                {/* Capsules */}
                <div className="case-study-capsules">
                    {capsules.map((cap, i) => (
                        <span key={i} className="case-study-capsule">
                            {cap}
                        </span>
                    ))}
                </div>

                {/* Image */}
                <div className="case-study-image">
                    {imageSrc ? (
                        <LazyImage
                            src={imageSrc}
                            alt={title}
                            className="case-study-image-inner"
                            placeholder={<div className="case-study-image-inner skeleton-bg" />}
                        />
                    ) : (
                        <div className="case-study-image-inner skeleton-bg" />
                    )}
                </div>

                {/* Content */}
                <div className="case-study-content">
                    <h3 className="case-study-title" title={title}>
                        {title}
                    </h3>
                    {description && (
                        <p className="case-study-description">{description}</p>
                    )}
                </div>
            </motion.div>
        </Link>
    )
}